<template>
    <div class="modalWrapper">
        <div class="searchFormWrapper">
            <form class="searchForm">
                <div class="heading">
                    <h2>Search criteria</h2>
                    <div class="nResults">
                        <template v-if="isNaN(nResults)">
                            Searching...
                        </template>
                        <template v-else>
                            {{ nResults }} properties found
                        </template>
                        <font-awesome-icon icon="fa-solid fa-spinner" class="fa-spin" v-if="loading" />
                    </div>
                    <div style="text-align: center; margin-left: auto;">
                        <button class="close" @click.prevent="$emit('close')">View results</button>
                        <br>
                        <a class="reset" @click="$emit('reset')">Clear search</a>
                    </div>
                </div>

                <div class="the-form">
                    <fieldset class="houseParty">
                        <legend>House and party</legend>
                        <div>
                            <label for="n_people">Party size</label>
                            <input type="number" id="n_people" v-model="criteria.n_people">
                        </div>

                        <div>
                            <label for="beds_min">Beds min</label>
                            <input type="number" id="beds_min" v-model="criteria.beds_min">

                            <label for="beds_max">Beds max</label>
                            <input type="number" id="beds_max" v-model="criteria.beds_max">
                        </div>

                        <div>
                            <label for="baths_min">Baths min</label>
                            <input type="number" id="baths_min" v-model="criteria.baths_min">

                            <label for="baths_max">Baths max</label>
                            <input type="number" id="baths_max" v-model="criteria.baths_max">
                        </div>

                        <div>
                            <label for="pets">Pets</label>
                            <input type="checkbox" id="pets" v-model="criteria.pets">
                        </div>

                        <div>
                            <label for="smoking">Smoking</label>
                            <input type="checkbox" id="smoking" v-model="criteria.smoking">
                        </div>

                        <div>
                            <label for="keywords">Keywords</label>
                            <input type="text" id="keywords" v-model="criteria.keywords">
                        </div>
                    </fieldset>

                    <fieldset class="towns">
                        <legend>Towns</legend>
                        <span v-if="!towns.length">
                            Loading...
                            <font-awesome-icon icon="fa-solid fa-spinner" class="fa-spin" />
                        </span>
                        <span v-for="town in towns" :key="town.slug" v-else>
                            <input v-model="criteria.towns" type="checkbox" :value="town.slug" :id="'town_' + town.slug">
                            <label :for="'town_' + town.slug" v-text="town.name" />
                        </span>
                    </fieldset>

                    <fieldset class="locales">
                        <legend>Locales</legend>
                        <span v-if="anyLoading && !locales.length">
                            Loading...
                            <font-awesome-icon icon="fa-solid fa-spinner" class="fa-spin" />
                        </span>
                        <span v-for="locale in locales" :key="locale.slug" v-else>
                                <input v-model="criteria.locales" type="checkbox" :value="locale.slug" :id="'locale_' + locale.slug">
                                <label :for="'locale_' + locale.slug" v-text="locale.label" />
                            </span>
                    </fieldset>

                    <fieldset class="amenities">
                        <legend>Amenities</legend>
                        <span v-if="anyLoading && !amenityGroups.length">
                            Loading...
                            <font-awesome-icon icon="fa-solid fa-spinner" class="fa-spin" />
                        </span>
                        <div v-for="ag in amenityGroups" class="amenityGroup" v-else>
                            <h2 v-text="ag.label" />
                            <span v-for="amenity in ag.amenities" :key="amenity.slug">
                                    <input v-model="criteria.amenities" type="checkbox" :value="amenity.slug" :id="'amenity_' + amenity.slug">
                                    <label :for="'amenity_' + amenity.slug" v-text="amenity.label" />
                                </span>
                        </div>
                    </fieldset>

                    <fieldset class="dates">
                        <legend>Dates</legend>
                        <div>
                            <input type="radio" name="dates_type" value="specific" id="dates_type_specific" v-model="criteria.dates_type">
                            <label for="dates_type_specific">Specific dates</label>

                            <label for="date_in">Check in</label>
                            <input type="date" id="date_in" v-model="criteria.date_in">

                            <label for="date_out">Check out</label>
                            <input type="date" id="date_out" v-model="criteria.date_out">
                        </div>
                        <div>
                            <input type="radio" name="dates_type" value="flexible" id="dates_type_flexible" v-model="criteria.dates_type">
                            <label for="dates_type_flexible">I'm flexible</label>

                            <label for="n_days">Stay days</label>
                            <input type="number" id="n_days" v-model="criteria.n_days">

                            <label for="date_earliest">Earliest arrival</label>
                            <input type="date" id="date_earliest" v-model="criteria.date_earliest">

                            <label for="date_latest">Latest departure</label>
                            <input type="date" id="date_latest" v-model="criteria.date_latest">
                        </div>
                        <div>
                            <input type="radio" name="dates_type" value="any" id="dates_type_any" v-model="criteria.dates_type">
                            <label for="dates_type_any">Show all open dates</label>
                        </div>
                    </fieldset>

                    <div class="clearfix"></div>

                    <fieldset class="rates">
                        <legend>Rates</legend>

                        <div>
                            <input type="radio" name="rate_type" value="nightly" id="rate_type_nightly" v-model="criteria.rate_type">
                            <label for="rate_type_nightly">Nightly</label>

                            <input type="radio" name="rate_type" value="weekly" id="rate_type_weekly" v-model="criteria.rate_type">
                            <label for="rate_type_weekly">Weekly</label>
                        </div>

                        <div>
                            <label for="rate_min">Rate min</label>
                            <input id="rate_min" v-model="criteria.rate_min">

                            <label for="rate_max">Rate max</label>
                            <input id="rate_max" v-model="criteria.rate_max">
                        </div>
                    </fieldset>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faSpinner, faCircleXmark } from '@fortawesome/free-solid-svg-icons'
library.add(faSpinner, faCircleXmark);

export default {
    components: {
        FontAwesomeIcon,
    },
    props: {
        value: {
            type: Object,
            required: true,
        },
        towns: {
            type: Array,
            default: () => [],
        },
        nResults: {
            type: Number,
        },
        loading: {
            type: Boolean,
            required: true,
        },
    },

    data() {
        return {
            // towns: [],
            locales: [],
            amenityGroups: [],
            myLoading: true,

            criteria: JSON.parse(JSON.stringify(this.value)),
            externalUpdate: false,
        };
    },

    computed: {
        anyLoading() {
            return this.loading || this.myLoading;
        },
    },

    watch: {
        value: {
            deep: true,
            handler() {
                this.externalUpdate = true;
                this.criteria = Object.assign({}, JSON.parse(JSON.stringify(this.value)));
                this.$nextTick().then(() => this.externalUpdate = false);
            },
        },
        criteria: {
            deep: true,
            handler() {
                if (!this.externalUpdate) {
                    console.debug('property searchform watch criteria', this.criteria);
                    this.$emit('input', this.criteria);
                }
            },
        },
    },

    created() {
        let vue = this;
        let promises = [];

        promises.push(
            axios.get('/api/locales')
                .then(response => vue.locales.splice(0, vue.locales.length, ...response.data))
        );
        promises.push(
            axios.get('/api/amenity-groups')
                .then(response => vue.amenityGroups.splice(0, vue.amenityGroups.length, ...response.data))
        );

        Promise.all(promises)
            .then(() => vue.myLoading = false)
        ;
    },
}
</script>

<style lang="scss">
.modalWrapper {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: rgba(0, 0, 0, 0.75);
    z-index: 10;
}

$form-height: calc(100vh - 150px);

.searchFormWrapper {
    background-color: white;
    padding: 0;
    width: 80vw;

    border-radius: 1vw;
    position: absolute;
    top: calc(50% + 50px);
    left: 50%;
    transform: translate(-50%, -50%);
    box-sizing: border-box;
    height: $form-height;
    overflow: auto;

    * {
        font-family: sans-serif;
    }

    .close {
        //position: absolute;
        //top: 5px;
        //right: 20px;
        //margin-left: auto;

        //cursor: default;
        //padding: 10px;
        //background-color: white;
        z-index: 1;

        color: white;
        background-color: #101030;
        height: 30px;
        padding: 0 10px;
        border-radius: 30px;
        font-size: 12pt;
        cursor: pointer;
        border-color: #101030;
    }

    .reset {
        color: #101030;
        margin: 10px 0;
        display: block;
        font-size: 10pt;
        cursor: pointer;
    }
}

.searchForm {
    margin: auto;
    display: flex;
    flex-direction: column;
    position: relative;
    height: 100%;
    max-height: $form-height;
    border-radius: 10px;

    .the-form {
        flex: 1;
        overflow: auto;
        padding: 10px 2vw 0 2vw;
        border: none;
    }

    * {
        font-family: sans-serif;
        font-size: 12pt;

        @media(max-width: 800px) {
            font-size: 1.6vw;
        }
    }

    .heading {
        display: flex;
        justify-content: left;
        column-gap: 20px;
        align-items: baseline;
        background-color: #ddd;
        padding: 0 2vw;

        & > * {
            flex: 0 0 auto;
        }
    }

    .nResults {
        font-style: italic;
    }


    label {
        display: inline-block;
    }
    fieldset {
        display: inline-flex;
        flex-direction: column;

        > div {
            margin-bottom: 5px;
        }
    }
    .houseParty {
        label {
            width: 7em;
        }
    }
    .towns, .locales, .amenities {
        display: block;
        columns: 2 16em;
        max-width: 34em;

        label {
            width: 16em;
        }
        & > span {
            display: block;
            white-space: nowrap;
        }
    }

    .amenities .amenityGroup:first-of-type h2 {
        margin-top: 0;
    }

    .amenityGroup {
        width: 18em;
        break-inside: avoid;
    }
}
</style>
